import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, Inject, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule } from '@ngx-translate/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { KnowledgeSource } from '@vendasta/embeddings';
import { AiKnowledgeService } from '../ai-knowledge.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AppsForKnowledgeSourceComponent } from '../apps-for-knowledge-source/apps-for-knowledge-source.component';
import { RouterModule } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { AiKnowledgeI18nModule } from '../assets/i18n/ai-knowledge-i18n.module';

export interface CustomKnowledgeData {
  knowledgeSource?: KnowledgeSource;
  readOnly?: boolean;
}

@Component({
  selector: 'ai-knowledge-custom-knowledge-editor-dialog',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    AiKnowledgeI18nModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    ReactiveFormsModule,
    FormsModule,
    GalaxyFormFieldModule,
    GalaxyLoadingSpinnerModule,
    RouterModule,
    AppsForKnowledgeSourceComponent,
  ],
  providers: [AiKnowledgeService],
  templateUrl: './custom-knowledge-editor.component.html',
  styleUrls: ['./custom-knowledge-editor.component.scss'],
})
export class CustomKnowledgeEditorComponent implements OnInit {
  private readonly knowledgeService = inject(AiKnowledgeService);
  form: FormGroup | undefined;
  private destroyRef = inject(DestroyRef);
  readonly isLoading = signal(true);

  constructor(
    public dialogRef: MatDialogRef<CustomKnowledgeEditorComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CustomKnowledgeData,
  ) {}

  ngOnInit(): void {
    this.form = new FormGroup({
      name: new FormControl(this.data?.knowledgeSource?.name || ''),
      content: new FormControl(this.data?.knowledgeSource?.config?.customDataConfig?.text || ''),
    });
    if (this.data?.readOnly) {
      this.form.disable();
    }

    this.form
      ?.get('selected')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((source) => {
        this.form?.get('name')?.setValue(source?.name || '');
        this.form?.get('content')?.setValue(source?.config?.customDataConfig?.content || '');
      });

    this.isLoading.set(false);
  }

  cancel(): void {
    this.dialogRef.close();
  }

  async save(): Promise<void> {
    const name = this.form.get('name').value;
    const content = this.form.get('content').value;
    if (this.data?.knowledgeSource) {
      if (!this.data.knowledgeSource.config?.customDataConfig) {
        return Promise.reject('Can only edit custom data knowledge sources.');
      }

      const source = this.data.knowledgeSource;
      source.name = name;
      source.config.customDataConfig.text = content;
      await this.knowledgeService.updateKnowledgeSourceNameAndConfig(source);
    } else {
      await this.knowledgeService.createKnowledgeSource(name, content);
    }

    this.dialogRef.close(true);
  }

  getTitle(): string {
    if (this.data?.readOnly && this.data?.knowledgeSource) {
      return this.data.knowledgeSource.name;
    }
    return 'INBOX.WEBCHAT.SETTINGS.CUSTOM_KNOWLEDGE.EDIT.TITLE';
  }

  buildManageKnowledgeUrl(): string {
    return this.knowledgeService.buildManageKnowledgeUrl();
  }

  buildEditQueryParams(): { editSource: string } {
    return { editSource: this.data?.knowledgeSource?.id || '' };
  }
}
